import { useEffect, useState } from 'react'
import { useGoogleFonts } from './briefFontSelection.hooks'
import { IGoogleFont, TGoogleFontCategory } from './briefFontSelection.types'
import { filterFonts, sortGoogleFonts } from './briefFontSelection.utils'
import { FontPreview } from './BriefFontSelectionPreview'
import { BriefFontSelectionToggle } from './BriefFontSelectionToggle'
import { BriefFontSelectionNav } from './BriefFontSelectionNav'
import { BriefFontSelectionList } from './BriefFontSelectionList'
import { BriefFontSelectionFilter } from './BriefFontSelectionFilter'
import { LoaderIcon } from '@noissue-ui-kit/oldBranding/loaderIcon'
import {
  usePreloadedAnswers,
  useReadOnlyMode,
} from '../../BriefingFormDataProvider'

export function BriefFontSelection({
  onChange,
}: {
  onChange: (value) => void
}) {
  const [dropdownIsOpen, setDropdownIsOpen] = useState<boolean>(false)
  const { googleFonts, loading } = useGoogleFonts()
  const [sortedFonts, setSortedFonts] =
    useState<Record<TGoogleFontCategory, IGoogleFont[]>>(null)
  const [activeCategory, setActiveCategory] =
    useState<TGoogleFontCategory>('all')
  const [displayedFonts, setDisplayedFonts] = useState<IGoogleFont[]>(null)
  const [filteredFonts, setFilteredFonts] = useState<IGoogleFont[]>(null)
  const [fontsFilter, setFontsFilter] = useState<string>(null)
  const [selectedFonts, setSelectedFonts] = useState<IGoogleFont[]>([])
  const preloadedAnswers = usePreloadedAnswers()
  const readOnlyMode = useReadOnlyMode()

  const selectFont = (font) => {
    const updatedSelectedFonts = [...new Set([...selectedFonts, font])]
    setSelectedFonts(updatedSelectedFonts)
    setDropdownIsOpen(false)
  }

  const unSelectFont = (font) => {
    const fontsSet = new Set(selectedFonts)
    fontsSet.delete(font)
    setSelectedFonts([...fontsSet])
  }

  useEffect(() => {
    if (googleFonts && googleFonts?.length) {
      setSortedFonts(sortGoogleFonts(googleFonts))
    }
  }, [googleFonts])

  useEffect(() => {
    if (activeCategory && sortedFonts) {
      setDisplayedFonts(sortedFonts[activeCategory])
    }
  }, [activeCategory, sortedFonts])

  useEffect(() => {
    if (fontsFilter) {
      setFilteredFonts(filterFonts(displayedFonts, fontsFilter))
    } else {
      setFilteredFonts(displayedFonts)
    }
  }, [fontsFilter, displayedFonts])

  useEffect(() => {
    onChange({
      'selected-fonts': selectedFonts?.map((font) => font.family) || [],
    })
  }, [selectedFonts])

  useEffect(() => {
    const preselectedFontNames = preloadedAnswers?.['selected-fonts']

    if (preselectedFontNames?.length && googleFonts?.length) {
      const preselectedFonts = preselectedFontNames.map((fontName) => {
        const font = googleFonts.find((font) => font.family === fontName)
        return font
      })
      if (preselectedFonts && preselectedFonts.length) {
        setSelectedFonts(preselectedFonts)
      }
    } else {
      setSelectedFonts([])
    }
  }, [preloadedAnswers, googleFonts])

  return (
    <div className="flex flex-col w-full font-mori">
      {!readOnlyMode && (
        <div
          className={`w-full border-2 bg-core-white border-black-20 rounded-[6px] cursor-pointer overflow-hidden transition-all mb-4 ${
            dropdownIsOpen ? 'max-h-[560px]' : 'max-h-[56px]'
          }`}
        >
          <BriefFontSelectionToggle
            dropdownIsOpen={dropdownIsOpen}
            onToggle={() => setDropdownIsOpen(!dropdownIsOpen)}
          />

          {loading ? (
            <div className="w-full h-[560px] flex select-none pt-32 px-6 pb-4 items-center justify-start flex-col">
              <LoaderIcon size="m" fillColorClass="fill-acai" />
              <span className="my-3 text-2xl text-acai">Loading fonts...</span>
            </div>
          ) : (
            <div className="w-full h-[560px] flex select-none px-6 pb-4 flex-col">
              <BriefFontSelectionNav
                activeCategory={activeCategory}
                categories={
                  Object.keys(sortedFonts || {}) as TGoogleFontCategory[]
                }
                onSelect={(category) =>
                  setActiveCategory(category as TGoogleFontCategory)
                }
              />

              <BriefFontSelectionFilter
                onChange={(filter) => setFontsFilter(filter)}
              />

              <BriefFontSelectionList
                fonts={filteredFonts}
                category={activeCategory}
                filter={fontsFilter}
                onSelection={(font) => selectFont(font)}
              />
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col">
        {selectedFonts?.length
          ? selectedFonts.map((font) => (
              <FontPreview
                {...font}
                isVisible={true}
                key={font.family + 'selected'}
                previewMode="selected"
                className="mb-4 text-acai"
                onUnSelect={() => {
                  if (!readOnlyMode) {
                    unSelectFont(font)
                  }
                }}
              />
            ))
          : ''}
      </div>
    </div>
  )
}
